import React, { useCallback, useEffect, useState } from 'react'
import { usePalette } from 'react-palette'
import { ReactComponent as StarIcon } from '@/assets/icons/star-icon.svg'
import { ReactComponent as TrashIcon } from '@/assets/icons/trash-icon.svg'
import { ReactComponent as UndoIcon } from '@/assets/icons/reply-icon.svg'
import { ReactComponent as BadgeIcon } from '@/assets/icons/badge-icon.svg'
import { ReactComponent as AddUserIcon } from '@/assets/icons/add-user-icon.svg'
import { ReactComponent as EllipsisIcon } from '@/assets/icons/ellipsis-icon.svg'
import { Badge } from '@/components/Badge/Badge'
import { HumanTime } from '@/components/commons/HumanTime'
import { ReactComponent as BoxesIcon } from '@/assets/icons/boxes-icon.svg'

import { HeaderDropdownMenu } from '../HeaderDropdownMenu/HeaderDropdownMenu'
import threadsService from '@/app-domains/threads/threads.service'
import dropdownService from '@/components/DropdownMenu/dropdown.service'
import store from '@/core/store'
import classNames from 'classnames'
import './TopHeader.scoped.sass'
import labelsService from '@/app-domains/labels/labels.service'
import { getImgSrcFromHTML, hexToRgb, rgbaToString } from '@/services/colorExtractor.service'
import boxesService from '@/app-domains/boxes/boxes.service'
import useRenderEvent from '@/core/hooks/useRenderEvent'
import modalService from '@/services/modal.service'
import CreateContactModal from '@/components/SideMenu/components/ContactMenu/components/CreateContactModal/CreateContactModal'
import contactsService from '@/app-domains/contacts/contacts.service'
import commonsService from '@/services/commons.service'

let timer = null
const timeToDelete = 5000

const startDeletingProcess = (message) => {
  timer = setTimeout(() => {
    threadsService.markThreadAsDeleted(message.threadId, true)
    threadsService.showUndoDeleteButton(message.threadId, message)
  }, timeToDelete)
}

const stopDeletingProcess = () => {
  clearTimeout(timer)
}

export function TopHeader (props) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [menuOpenedEvent, setMenuOpenedEvent] = useState(false)
  const [isDeleted, setIsDeleted] = useState(props.thread.isDeleted)
  const [isDeleting, setIsDeleting] = useState(false)

  const toggleMenuOpen = (e) => {
    if (dropdownService.isOpenedForThisTarget(e)) {
      setMenuOpened(false)
      setMenuOpenedEvent(false)
      return dropdownService.close()
    }
    e.stopPropagation()
    setMenuOpenedEvent(e)
    setMenuOpened(true)
  }

  useEffect(() => {
    store.on('DROPDOWN:CLOSE', () => {
      setMenuOpened(false)
      setMenuOpenedEvent(false)
    })
  }, [])

  useEffect(() => {
    setIsDeleted(props.thread.isDeleted)
  }, [props.thread])

  const {
    thread,
    colorData,
    viewType,
    darkenHeaderIfDense,
    hideMessageBody
  } = props

  const [message, setMessage] = useState(props.message)

  useRenderEvent([`THREAD:${thread.composedid}:UPDATE:READ`], () => {
    setMessage({ ...message, unopen: false })
  })

  const [box] = useState(boxesService.findBoxbyId(message.boxid))
  const [labels] = useState(labelsService.findLabelsForBox(box))

  const pinMessage = useCallback(() => {
    threadsService.setThreadPinned(message.threadId, !thread.isPinned)
  }, [message, thread])

  const undoDelete = useCallback(() => {
    threadsService.setThreadDeleted(message.threadId, message, isDeleted, isDeleting)
    setIsDeleted(false)
    hideMessageBody(false)
    stopDeletingProcess()
  }, [message, thread, isDeleting, isDeleted])

  const toggleMessageDeleted = useCallback(() => {
    threadsService.setThreadDeleted(message.threadId, message, !isDeleted, false)
    hideMessageBody(true)
    setIsDeleted(true)
    setIsDeleting(true)
    startDeletingProcess(message)
  }, [message, thread, isDeleting, isDeleted])

  const createContact = () => {
    const contactProperties = {
      emails: [getFromEmailOfMainMessageOfThread(thread)]
    }
    modalService.open(<CreateContactModal type='new' dataToFillInCreationForm={contactProperties}/>, true)
  }

  const getFromEmailOfMainMessageOfThread = (thread) => {
    return thread.messages.filter((message) => message.isHeadOfThread)[0]?.fromEmail
  }

  const shouldShowContactCreation = (thread) => {
    return commonsService.isMobile()
      ? false
      : !contactsService.findContactByEmail(
          getFromEmailOfMainMessageOfThread(thread)
        )
  }
  // eslint-disable-next-line
  function getRgbaColor () {
    const img_src = message.html ? getImgSrcFromHTML(message.html) : null
    const { data = null } = usePalette(img_src) || {}
    return data?.vibrant ? rgbaToString(...hexToRgb(data.vibrant), 0.5) : null
  }
  
  const getBackgroundColor = useCallback(() => {
    // if (viewType !== 'dense') { return colorData.vibrant }
    if (viewType === 'dense') return darkenHeaderIfDense ? '#E0E8FF' : null
    // return getRgbaColor()
  }, [viewType, colorData])

  return <div className={classNames('top-header', { [viewType]: viewType, read: !message.unopen })} style={{
    backgroundColor: getBackgroundColor()
  }} onClick={props.toggleMessageOnClick}>
    <div className="top-header-title">
      <div className="top-header-left">
        <div className="subject-title">
          {message.subject || <i className="no-subject">No subject</i>}
        </div>
        <TopHeaderBadgeSection box={box} labels={labels} viewType={viewType} message={message} thread={thread}/>
      </div>
      <div className="top-header-right">
        { shouldShowContactCreation(thread) && <AddUserIcon className={classNames('star cursor-pointer')} onClick={createContact}/> }
        <StarIcon className={classNames('star cursor-pointer', { active: props.thread.isPinned })} onClick={pinMessage} />
        { !isDeleted && <TrashIcon className={classNames('star cursor-pointer')} onClick={toggleMessageDeleted} />}
        { isDeleted && <UndoIcon className={classNames('star cursor-pointer')} onClick={undoDelete} />}
        <div className='dropdown-menu-button' onClick={toggleMenuOpen}>
          <EllipsisIcon/>
          {menuOpened && <HeaderDropdownMenu message={message} setMessage={setMessage} thread={thread} event={menuOpenedEvent}/>}
        </div>
      </div>
    </div>
    {viewType === 'dense' && <div className="top-header-info">
      <div className="contact-name">
        {message?.fromEmailOnlyName}
      </div>
      <div className="date">
        <HumanTime date={message?.date}/>
      </div>
      </div>}

  </div>
}

export function TopHeaderBadgeSection (props) {
  const [areAllBadgesShown, setAreAllBadgesShown] = useState(false)
  const {
    box,
    labels,
    viewType,
    message,
    thread
  } = props

  const applyBoxFilterFromBadge = useCallback(() => {
    threadsService.applyFilterFor('Box', message.boxName, message.boxid)
  }, [message, thread])

  const applyLabelFilterFromBadge = useCallback((label) => {
    if (label.name === 'Promotional') {
      return threadsService.applyFilterFor('Promotional', 'Promotional', 'Promotional')
    }
    threadsService.applyFilterFor('Label', label.name, label.composedid)
  }, [labels, message, thread])

  const toggleShowAllBadges = () => setAreAllBadgesShown(!areAllBadgesShown)

  const generateLabelChips = (labels, showAll = false) => {
    if (thread.isPromotional) {
      if (!labels.find(label => label.name === 'Promotional')) {
        labels.push({ name: 'Promotional' })
      }
    }
    if (labels.length >= 3) {
      const remainingLabels = labels.length - 2
      if (!showAll) {
        const visibleLabels = labels.slice(0, 2)
        const visibleLabelsAsComponents = visibleLabels.map(label => <Badge key={label.composedid} onClick={() => applyLabelFilterFromBadge(label)}><BadgeIcon/>{label.name}</Badge>)
        return [...visibleLabelsAsComponents, <Badge key='123123123123123' onClick={toggleShowAllBadges}>+{remainingLabels}</Badge>]
      }
      const visibleLabelsAsComponents = labels.map(label => <Badge key={label.composedid} onClick={() => applyLabelFilterFromBadge(label)}><BadgeIcon/>{label.name}</Badge>)
      return [...visibleLabelsAsComponents, <Badge key='123123123123123' onClick={toggleShowAllBadges}>Hide last {remainingLabels > 1 ? `${remainingLabels} labels` : 'label'}</Badge>]
    }
    return labels.map(label => <Badge key={label.composedid} onClick={() => applyLabelFilterFromBadge(label)}><BadgeIcon/>{label.name}</Badge>)
  }

  return viewType === 'normal' && <div className="subject-labels">
    <Badge onClick={applyBoxFilterFromBadge}><BoxesIcon/>{box.boxname}</Badge>
    {generateLabelChips(labels, areAllBadgesShown)}
    </div>
}
